<template>
  <view-container>
    <token-packs
      v-if="routeName === MOD_BROWSE_ROUTE_TOKEN_PACKS"
      :game="game"
    />

    <browse-list
      v-else
      :game="game"
      :items="modList"
      :loading="isLoading"
      :browse-id="browseId"
      :total="modList?.result_total"
      :sort-options="MOD_SORT_OPTIONS"
      :card="MOD_CARD"
      @fetch:paginate-next="fetchPaginateNext"
      @fetch:query="fetchMods"
    />
  </view-container>
</template>

<script>
import { usePermissions, useFetchSubs, useAsync, useRoute } from '@composables'
import { checkPartnerProgramStatus } from '@helpers/monetizationUtils.js'
import TokenPacks from '@components/Monetization/Panels/TokenPacks.vue'
import { MOD_FREE_AND_PAID_WEIGHTED } from '@config/monetization.js'
import ViewContainer from '@components/View/ViewContainer.vue'
import { mergeApiResultData } from '@helpers/requestAll.js'
import { filterSettings, Settings } from '@config/game.js'
import BrowseList from '@components/List/BrowseList.vue'
import { MOD_SORT_OPTIONS } from '@config/sort.js'
import { getModsForGameRequest } from '@services'
import { KEYS_MODS } from '@config/storeKeys.js'
import { computed, provide, watch } from 'vue'
import { MOD_CARD } from '@config/card.js'
import useFilter, {
  getContentTypeFilter,
  normalizeModFilters,
  DATE_FILTER_ITEMS,
  REVENUE_TYPE_MAP,
  getDateFilter,
  DATE_LIVE_MAP,
  getAPIFilter,
} from '@composables/useFilter.js'
import {
  isMonetizationPartnerProgramPreviewEnabled,
  isTokenMarketplaceEnabled,
  isUSDMarketplaceEnabled,
  isMonetizationEnabled,
} from '@helpers/bitHelper.js'
import {
  partnerProgramStore,
  userStatsStore,
  gameStore,
  authStore,
  userStore,
  modStore,
  store,
} from '@stores'
import {
  MOD_BROWSE_ROUTE_TOKEN_PACKS,
  USER_MOD_BROWSE_ROUTE,
} from '@config/routeNames.js'

export default {
  components: {
    ViewContainer,
    BrowseList,
    TokenPacks,
  },
  setup() {
    const { getPublicGame } = gameStore()
    const { routeQuery, getUserNameId, routeName } = useRoute()
    const { fetchPurchases, fetchSubs } = useFetchSubs()
    const { isGameModerator } = usePermissions()
    const { addMods, getMods } = modStore()

    const { addUserStats } = userStatsStore()
    const userNameId = getUserNameId().value
    const { isLoggedIn, user: myUser } = authStore()

    const { isInPartnerProgram } = partnerProgramStore()
    const user = userStore().getUser(userNameId)
    const { generateId } = store()
    const game = getPublicGame()

    const browseId = generateId(
      KEYS_MODS,
      `${game.value.name_id}${userNameId || ''}`
    )
    const { setFilter, settingsTags, resetFilter } = useFilter(browseId)

    provide('settingsTags', settingsTags)

    const mods = getMods(`${game.value.name_id}${userNameId || ''}`)

    const modList = computed(() => mods.value)

    const isMe = computed(
      () => isLoggedIn.value && myUser.value?.name_id === userNameId
    )

    const showAdminFilters = computed(() => isGameModerator.value || isMe.value)

    const marketplaceEnabled = computed(
      () =>
        Settings.value.showMarketplace &&
        isMonetizationEnabled(game.value.monetization_options) &&
        (isTokenMarketplaceEnabled(game.value.monetization_options) ||
          isUSDMarketplaceEnabled(game.value.monetization_options)) &&
        (!isMonetizationPartnerProgramPreviewEnabled(
          game.value.monetization_options
        ) ||
          isInPartnerProgram(game.value.id).value)
    )

    const {
      loading: loadingMods,
      run: runFetchMods,
      error: modErrors,
      data: modsData,
    } = useAsync((query) => getModsForGameRequest(null, query))

    const isLoading = computed(() => loadingMods.value)

    watch(
      [game, showAdminFilters],
      ([_game, _showAdminFilters]) => {
        if (_game) {
          const filters = _game.tag_options.filter(
            (tagGroup) => !tagGroup.hidden || isGameModerator.value
          )
          const queryTags = routeQuery.value.tags
          const tagsNotIn = routeQuery.value['tags-not-in'] || ''

          const dateQuery =
            routeQuery.value.timespan ||
            DATE_LIVE_MAP[routeQuery.value.date_live]
          const dateTags = DATE_FILTER_ITEMS.includes(dateQuery)
            ? dateQuery
            : ''
          const contentType =
            routeQuery.value.content_type ||
            REVENUE_TYPE_MAP[routeQuery.value.revenue_type] ||
            ''
          const tags = [filterSettings.value.tags, queryTags]
            .filter((tag) => tag)
            .join(',')

          const adminFilters = [routeQuery.value.admin]
            .filter((filter) => filter)
            .join(',')

          setFilter(
            normalizeModFilters(
              filters,
              {
                tags,
                'tags-not-in': tagsNotIn,
                date_live: dateTags,
                content_type: contentType,
                admin: adminFilters,
              },
              _showAdminFilters,
              {
                marketplaceEnabled: marketplaceEnabled.value,
              }
            ),
            !queryTags && !dateTags && !contentType
          )

          checkPartnerProgramStatus(game.value)
        }
      },
      {
        immediate: true,
      }
    )

    watch(() => filterSettings.value.tags, resetFilter)

    function getQuery(query = {}) {
      const _query = { ...query }

      delete _query.platforms

      if (userNameId) {
        _query.submitted_by = user.value.id
        _query.revenue_type = MOD_FREE_AND_PAID_WEIGHTED
      }

      getAPIFilter(_query)
      getDateFilter(_query)

      if (marketplaceEnabled.value) {
        if (_query.content_type) {
          getContentTypeFilter(_query)
        } else {
          _query.revenue_type = MOD_FREE_AND_PAID_WEIGHTED
        }
      }

      if (
        userNameId &&
        isMonetizationPartnerProgramPreviewEnabled(
          game.value.monetization_options
        ) &&
        !isInPartnerProgram(game.value.id).value &&
        !isGameModerator.value
      ) {
        _query.price = 0
      }

      return _query
    }

    async function fetchMods(query) {
      const _query = getQuery(query)

      await runFetchMods(_query)

      if (!modErrors.value) {
        _updateStore(modsData.value, query)

        if (routeName.value === USER_MOD_BROWSE_ROUTE) {
          const total = modsData.value.result_total
          addUserStats(userNameId, { mod_count: total })
        }
      }

      fetchSubs(false)
      if (marketplaceEnabled.value) fetchPurchases(false)
    }

    async function fetchPaginateNext(query) {
      const _query = getQuery(query)

      await runFetchMods(_query)

      if (!modErrors.value) {
        _updateStore(mergeApiResultData(mods.value, modsData.value), query)
      }
    }

    function _updateStore(data, query) {
      addMods(`${game.value.name_id}${userNameId || ''}`, data, query)
    }

    return {
      MOD_BROWSE_ROUTE_TOKEN_PACKS,
      fetchPaginateNext,
      MOD_SORT_OPTIONS,
      routeName,
      isLoading,
      fetchMods,
      browseId,
      MOD_CARD,
      modList,
      game,
    }
  },
}
</script>
